import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Target } from "lucide-react";
import type { CompanyProfile } from "@shared/schema";
import { missingRefineAnswers, type StrategyRequestResponse } from "@/lib/strategyRefine";

// "Ihre Strategie schärfen" (Shade option A, texts approved by Mike 04.10.): the foundation questions the
// strategy prompt reads, in hotel language. Save = PATCH /api/companies/me/ai-fields (targetAudience,
// valueProposition), then POST /api/strategy/trigger-generation with the three required answers. The server
// records the run with source "foundation", so the result counts as refined and the cockpit banner goes away.

export const REFINE_ANCHOR = "strategie-schaerfen";
const MAX = 2000;

type Answers = {
  customerMainProblem: string;
  customerEnemy: string;
  contrarianPov: string;
  targetAudience: string;
  valueProposition: string;
};

type Question = { key: keyof Answers; label: string; help?: string; example: string; optional?: boolean };

const QUESTIONS: Question[] = [
  {
    key: "customerMainProblem",
    label: "Was beschäftigt Ihre Gäste, bevor sie bei Ihnen buchen?",
    help: "Welchen Wunsch oder welches Problem haben Ihre Gäste, wenn sie ein Hotel suchen?",
    example: "z. B. Sie wollen endlich abschalten, finden aber nur austauschbare Häuser ohne Charakter.",
  },
  {
    key: "customerEnemy",
    label: "Wogegen stehen Sie gemeinsam mit Ihren Gästen?",
    help: "Was steht einem gelungenen Aufenthalt im Weg? Das kann eine Gewohnheit, ein Trend oder ein Ärgernis der Branche sein.",
    example: "z. B. Massenabfertigung in großen Bettenburgen, Urlaub nach Checkliste, ständige Erreichbarkeit.",
  },
  {
    key: "contrarianPov",
    label: "Was sehen Sie anders als die meisten Hotels?",
    help: "Ihre Überzeugung, mit der Sie sich von anderen Häusern abheben.",
    example: "z. B. Erholung braucht keinen großen Wellnessbereich. Ruhe, gutes Essen und herzliche Gastgeber reichen.",
  },
  {
    key: "targetAudience",
    label: "Wer sind Ihre Wunschgäste?",
    example: "z. B. Paare ab 50 aus Süddeutschland, die zwei bis drei Nächte bleiben und gern wandern.",
    optional: true,
  },
  {
    key: "valueProposition",
    label: "Warum buchen Gäste gerade bei Ihnen?",
    help: "Ihr Versprechen in einem Satz.",
    example: "z. B. Bei uns kommen Sie nach einem Tag zur Ruhe, weil es kein Programm gibt, nur Natur und Zeit.",
    optional: true,
  },
];

export const REFINE_MESSAGES = {
  missing: "Bitte beantworten Sie die drei Fragen zu Ihren Gästen.",
  running: "Ihre Strategie wird gerade erstellt. Bitte versuchen Sie es in einigen Minuten erneut.",
  success: "Ihr Team schärft jetzt Ihre Strategie. Die neue Version finden Sie in etwa 10 Minuten unter Marketing-Strategie.",
  failed: "Das hat gerade nicht geklappt. Ihre Antworten sind noch da, bitte versuchen Sie es erneut.",
  // Hotels: the server routes the answers to the team instead of the strategy engine (teamRequest).
  team: "Ihr Team überarbeitet Ihre Strategie, Sie erhalten sie innerhalb von 2 Werktagen.",
};

function initialAnswers(company: CompanyProfile | undefined): Answers {
  return {
    customerMainProblem: company?.customerMainProblem ?? "",
    customerEnemy: company?.customerEnemy ?? "",
    contrarianPov: company?.contrarianPov ?? "",
    targetAudience: company?.targetAudience ?? "",
    valueProposition: company?.valueProposition ?? "",
  };
}

export function StrategyRefineSection({ company }: { company: CompanyProfile | undefined }) {
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Answers>(() => initialAnswers(company));
  const [message, setMessage] = useState<{ tone: "error" | "ok"; text: string } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  // Hotels: keep showing the 2-Werktage hint after a reload while the request to the team is open.
  const { data: strategyState } = useQuery<{ teamRequestOpen?: boolean }>({ queryKey: ["/api/strategy"], retry: false });
  const shownMessage = message ?? (strategyState?.teamRequestOpen ? { tone: "ok" as const, text: REFINE_MESSAGES.team } : null);

  // The cockpit banner links to /a-team-briefing#strategie-schaerfen; client-side navigation does not scroll.
  useEffect(() => {
    if (window.location.hash === `#${REFINE_ANCHOR}`) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const save = useMutation({
    mutationFn: async (a: Answers) => {
      await apiRequest("PATCH", "/api/companies/me/ai-fields", {
        targetAudience: a.targetAudience.trim(),
        valueProposition: a.valueProposition.trim(),
      });
      return apiRequest("POST", "/api/strategy/trigger-generation", {
        customerMainProblem: a.customerMainProblem.trim(),
        customerEnemy: a.customerEnemy.trim(),
        contrarianPov: a.contrarianPov.trim(),
      });
    },
    onSuccess: (res: StrategyRequestResponse) => {
      const text = res?.teamRequest ? REFINE_MESSAGES.team : REFINE_MESSAGES.success;
      setMessage({ tone: "ok", text });
      toast({ title: res?.teamRequest ? "Anfrage an Ihr Team gesendet" : "Strategie wird geschärft", description: text });
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/status"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/strategy"] });
    },
    onError: (err: any) => {
      // Hotel request whose ops mail failed: the server text says the answers are saved, try again later.
      if (err?.data?.teamRequest && err?.data?.error) setMessage({ tone: "error", text: err.data.error });
      else if (err?.status === 409) setMessage({ tone: "error", text: REFINE_MESSAGES.running });
      else if (err?.status === 400) setMessage({ tone: "error", text: REFINE_MESSAGES.missing });
      else setMessage({ tone: "error", text: REFINE_MESSAGES.failed });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (missingRefineAnswers(answers)) {
      setMessage({ tone: "error", text: REFINE_MESSAGES.missing });
      return;
    }
    setMessage(null);
    save.mutate(answers);
  };

  return (
    <div ref={ref} id={REFINE_ANCHOR} className="mb-6 scroll-mt-6">
      <Card data-testid="card-strategy-refine">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <CardTitle>Ihre Strategie schärfen</CardTitle>
          </div>
          <CardDescription>
            Ihre erste Strategie ist vorläufig. Sie entstand aus Ihrer Website und Ihren Angaben im Check-In.
            Mit Ihren Antworten hier schärft Ihr Team sie und erstellt eine neue Version.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-6" noValidate>
            {QUESTIONS.map((q) => (
              <div key={q.key} className="space-y-2">
                <Label htmlFor={`refine-${q.key}`} className="text-base font-semibold">
                  {q.label}
                  {q.optional && <span className="ml-1 font-normal text-muted-foreground">(optional)</span>}
                </Label>
                {q.help && <p className="text-sm text-muted-foreground">{q.help}</p>}
                <Textarea
                  id={`refine-${q.key}`}
                  value={answers[q.key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAnswers((a) => ({ ...a, [q.key]: v }));
                    setMessage(null);
                  }}
                  placeholder={q.example}
                  maxLength={MAX}
                  className="min-h-[90px]"
                  data-testid={`textarea-refine-${q.key}`}
                />
              </div>
            ))}

            {shownMessage && (
              <div
                role={shownMessage.tone === "error" ? "alert" : "status"}
                className={
                  shownMessage.tone === "error"
                    ? "rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                    : "rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900"
                }
                data-testid="text-refine-message"
              >
                {shownMessage.text}
              </div>
            )}

            <div className="flex justify-end">
              <Button type="submit" disabled={save.isPending} data-testid="button-refine-strategy">
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Strategie schärfen"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
